import React, { useContext, useEffect, useState, Suspense, memo } from 'react';
import { useModal } from '../hooks/useModal';
import { lazyWithRetry as lazy } from '../utils/chunkLoad';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import { AppContext } from '../window';
import { Link, useNavigate, useLocation } from 'react-router';
import { PersonCircle, Chat, CreditCardFill, DiamondFill, BoxArrowRight, Search, HeartFill, StarFill, PatchCheckFill, GearFill, PersonPlusFill, BoxArrowInRight, BoxArrowInLeft, InfoCircleFill, ThreeDotsVertical, KeyFill, MoonFill, SunFill, ChevronDown, EyeFill } from 'react-bootstrap-icons';

// Lazy load auth modals - they're not needed until user clicks login/signup
const LoginModal = lazy(() => import('../user/LoginModal'));
const SignupModal = lazy(() => import('../user/SignupModal'));
const RequestResetModal = lazy(() => import('../user/RequestResetModal'));
const TermsConditionsModal = lazy(() => import('../about/TermsConditionsModal'));
const PrivacyPolicyModal = lazy(() => import('../about/PrivacyPolicyModal'));
import { NavigationContext } from './NavigationContext';
import Avatar from '../libs/AppAvatar';
import { useConversationContext } from '../context/ConversationContext';
import { NotificationContext } from '../context/NotificationContext';
import { useArchiveContext } from '../context/ArchiveContext';
import { performLogout } from '../utils/logout';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../i18n';

interface NavItemsProps {
  onSearchClick?: () => void;
  handleSearchClick: (e: React.MouseEvent) => void;
  unreadMessageCount?: number;
}
const NavItems: React.FC<NavItemsProps> = memo(({ onSearchClick, handleSearchClick, unreadMessageCount }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const isActive = (path: string) => {
    // For root, match exactly
    if (path === '/') {
      return location.pathname === '/';
    }
    // For others, match if starts with path
    return location.pathname.startsWith(path);
  };
  return (
    <>
      <Nav.Link as={Link} to="/search" className={`me-lg-4${isActive('/search') ? ' active' : ''}`} onClick={handleSearchClick}><Search size={16} /> {t('general.search')}</Nav.Link>
      <Nav.Link as={Link} to="/inbox" className={`me-lg-4${isActive('/inbox') ? ' active' : ''}`}>
        <Chat size={16} /> {t('general.chats')}
        {unreadMessageCount && unreadMessageCount > 0 ? (
          <Badge bg="primary" pill className="ms-1">{unreadMessageCount}</Badge>
        ) : null}
      </Nav.Link>
      <Nav.Link as={Link} to="/match" className={`me-lg-4${isActive('/match') ? ' active' : ''}`}><HeartFill size={16} /> {t('general.match')}</Nav.Link>
    </>
  );
});

NavItems.displayName = 'NavItems';

interface NavigationProps {
  landingPageMode?: boolean;
  loginText?: string;
  hideLogin?: boolean;
}

const Navigation: React.FC<NavigationProps> = memo(({ landingPageMode = false, loginText = 'Login', hideLogin = false }) => {
  const { t } = useTranslation();
  const { logo, user, setUser } = useContext(AppContext);
  const { hideFirstNavbar, hideSecondNavbar, onSearchClick } = useContext(NavigationContext);
  const { resetConversations } = useConversationContext();
  const { notifications, removeNotification } = useContext(NotificationContext);
  const { resetArchive } = useArchiveContext();
  const loginModal = useModal();
  const signupModal = useModal();
  const passwordResetModal = useModal();
  // The address typed in the login modal, prefilled when "Forgot password?" opens the reset modal
  const [ resetEmail, setResetEmail ] = useState('');
  const openPasswordReset = () => { setResetEmail(''); passwordResetModal.open(); };
  const termsModal = useModal();
  const privacyModal = useModal();
  const [ isPremium, setIsPremium ] = useState(false);
  const { react } = useContext(AppContext);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const avatarSrc = user?.displayPicture?.thumb || undefined;
  const logoSrc = typeof logo?.src === 'string' && logo.src.trim() !== '' ? logo.src : undefined;


  useEffect(() => {
    if(!user?.wallet?.premiumExpiry) {
      setIsPremium(false);
      return;
    }
    setIsPremium(new Date(user.wallet.premiumExpiry) > new Date());
  }, [user?.wallet?.premiumExpiry]);

  
  const navigate = useNavigate();

  const handleSearchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onSearchClick) {
      onSearchClick();
    } else {
      navigate(user ? '/search' : '/');
    }
  };

  const handleLogout = async () => {
    await performLogout({
      navigate,
      setUser,
      resetConversations,
      resetArchive,
      notifications,
      removeNotification,
      reactConfig: react.config
    });
  };


  return (
    <>
      <Navbar 
        expand 
        className={`bg-body primary-nav primary-nav-transition pt-1 pb-1 pb-lg-2 ${hideFirstNavbar ? 'primary-nav--hidden' : ''}`}
        aria-hidden={hideFirstNavbar || undefined}
      >
        <Container>
          <Navbar.Brand
            onClick={handleSearchClick}
            style={{ cursor: 'pointer' }}
          >
            <img
              src={logoSrc}
              loading="lazy"
              style={{ height: logo.height }}
              className="d-inline-block align-top"
              alt={t('general.logo_alt')}
            />
          </Navbar.Brand>

          {landingPageMode && !user ? (
            <Nav className="ms-auto">
              {!hideLogin && <Nav.Link as={Link} to="#" className="btn btn-primary" onClick={loginModal.open}><BoxArrowInRight size={16} /> {loginText}</Nav.Link>}
            </Nav>
          ) : user ? (
            <>
              <Nav className="d-none d-lg-flex">
                <NavItems onSearchClick={onSearchClick} handleSearchClick={handleSearchClick} unreadMessageCount={user.unreadMessageCount} />
              </Nav>
              <Nav>
                <Nav.Link as={Link} to="/credits" className={location.pathname === '/credits' ? 'active' : ''} style={{lineHeight: '38px'}}><CreditCardFill size={16} /><span className="d-none d-lg-inline-block ms-1">{t('general.credits')}</span> </Nav.Link>
                <NavDropdown
                  title={(
                    <div className="d-inline-flex align-items-center">
                      <div className="position-relative d-inline-block">
                        <Avatar
                          src={avatarSrc}
                          circle
                          size="md"
                          {...(isPremium ? { pulsingBorder: true, pulsingBorderColor: 'warning' } : {})}
                        />
                        {user.credentials?.emailStatus > 0 && (
                          <PatchCheckFill 
                            size={20}
                            className="position-absolute top-0 end-0 text-success bg-white rounded-circle p-1"
                            style={{ zIndex: 1000,marginRight: '-12px',marginTop: '-6px' }}
                          />
                        )}
                        {/* Bubble with chevron overlay bottom-right */}
                        <span className="nav-avatar-bubble" aria-hidden="true">
                          <ChevronDown className="nav-avatar-bubble-icon" size={12} />
                        </span>
                      </div>
                    </div>
                  )}
                  id="menu-dropdown"
                  align="end"
                  className="theme-dropdown"
                >
                  {/* User info section */}
                  {user.wallet?.premiumExpiry && new Date(user.wallet.premiumExpiry) > new Date() && (
                    <NavDropdown.Item disabled style={{ color: '#bfa100', fontWeight: 'bold', background: '#fffbe6' }}>
                      <DiamondFill style={{ color: '#bfa100', marginRight: 4 }} size={16} /> {t('general.premium_member')}
                    </NavDropdown.Item>
                  )}
                  <NavDropdown.Item as={Link} to="/profile" style={location.pathname === '/profile' ? { color: 'var(--bs-primary)' } : {}}><PersonCircle size={16} /> {t('general.profile')}</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/favorites" style={location.pathname === '/favorites' ? { color: 'var(--bs-primary)' } : {}}><StarFill size={16} /> {t('general.my_favorites')}</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/visitors" style={location.pathname === '/visitors' ? { color: 'var(--bs-primary)' } : {}}><EyeFill size={16} /> {t('general.profile_visitors')}</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/settings" style={location.pathname === '/settings' ? { color: 'var(--bs-primary)' } : {}}><GearFill size={16} /> {t('general.settings')}</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/credits" style={location.pathname === '/credits' ? { color: 'var(--bs-primary)' } : {}}><CreditCardFill size={16} /> {t('general.buy_credits')}</NavDropdown.Item>
                  <NavDropdown.Divider />
<NavDropdown.Item as={Link} to="/about" style={location.pathname === '/about' ? { color: 'var(--bs-primary)' } : {}}><InfoCircleFill size={16} /> {t('general.about_faq')}</NavDropdown.Item>
                  <NavDropdown.Divider />
                  <div className="px-3 py-2">
                    <Form.Check
                      type="switch"
                      id="theme-switch-member"
                      label={
                        <span className="d-flex align-items-center">
                          {theme === 'light' ? <SunFill size={16} className="me-2" /> : <MoonFill size={16} className="me-2" />}
                          {theme === 'light' ? t('general.light_mode') : t('general.dark_mode')} {t('general.mode_suffix')}
                        </span>
                      }
                      checked={theme === 'dark'}
                      onChange={toggleTheme}
                      className="mb-0"
                    />
                  </div>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}><BoxArrowRight size={16} /> {t('general.logout')}</NavDropdown.Item>
                </NavDropdown>
              </Nav>
            </>
          ) : (
            <Nav className="ms-auto d-none d-lg-flex">
              <Nav.Link as={Link} to="#" className="btn btn-outline-primary me-2" onClick={signupModal.open}><PersonPlusFill size={16} /> {t('general.sign_up')}</Nav.Link>
              <Nav.Link as={Link} to="#" className="btn btn-primary" onClick={loginModal.open}><BoxArrowInLeft size={16} /> {t('general.login')}</Nav.Link>
              <NavDropdown
                title={<ThreeDotsVertical size={20} />}
                id="guest-menu-dropdown"
                align="end"
                className="theme-dropdown"
              >
                <NavDropdown.Item onClick={openPasswordReset}><KeyFill size={16} /> {t('general.reset_password')}</NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item as={Link} to="/about" style={location.pathname === '/about' ? { color: 'var(--bs-primary)' } : {}}><InfoCircleFill size={16} /> {t('general.about_faq')}</NavDropdown.Item>
                <NavDropdown.Divider />
                <div className="px-3 py-2">
                  <Form.Check
                    type="switch"
                    id="theme-switch-guest"
                    label={
                      <span className="d-flex align-items-center">
                        {theme === 'light' ? <SunFill size={16} className="me-2" /> : <MoonFill size={16} className="me-2" />}
                        {theme === 'light' ? t('general.light_mode') : t('general.dark_mode')} {t('general.mode_suffix')}
                      </span>
                    }
                    checked={theme === 'dark'}
                    onChange={toggleTheme}
                    className="mb-0"
                  />
                </div>
              </NavDropdown>
            </Nav>
          )}
        </Container>
  </Navbar>

      {!hideSecondNavbar && !landingPageMode && user && (
        <Navbar expand className="bg-body primary-nav d-lg-none pt-0">
          <Container className="d-flex justify-content-center">
            <Nav>
              <NavItems onSearchClick={onSearchClick} handleSearchClick={handleSearchClick} unreadMessageCount={user.unreadMessageCount} />
            </Nav>
          </Container>
        </Navbar>
      )}

      {!hideSecondNavbar && !landingPageMode && !user && (
        <Navbar expand className="bg-body primary-nav d-lg-none pt-0">
          <Container className="d-flex position-relative">
            <Nav className="w-100 d-flex justify-content-center align-items-center">
              <Nav.Link as={Link} to="#" className="btn btn-outline-primary me-2" onClick={signupModal.open}><PersonPlusFill size={16} /> {t('general.sign_up')}</Nav.Link>
              <Nav.Link as={Link} to="#" className="btn btn-primary" onClick={loginModal.open}><BoxArrowInLeft size={16} /> {t('general.login')}</Nav.Link>
            </Nav>
            <NavDropdown
              title={<ThreeDotsVertical size={20} />}
              id="guest-menu-dropdown-mobile"
              align="end"
              className="position-absolute end-0 pe-3 theme-dropdown"
            >
              <NavDropdown.Item onClick={openPasswordReset}><KeyFill size={16} /> {t('general.reset_password')}</NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item as={Link} to="/about" style={location.pathname === '/about' ? { color: 'var(--bs-primary)' } : {}}><InfoCircleFill size={16} /> {t('general.about')}</NavDropdown.Item>
              <NavDropdown.Divider />
              <div className="px-3 py-2">
                <Form.Check
                  type="switch"
                  id="theme-switch-guest-mobile"
                  label={
                    <span className="d-flex align-items-center">
                      {theme === 'light' ? <SunFill size={16} className="me-2" /> : <MoonFill size={16} className="me-2" />}
                      {theme === 'light' ? t('general.light_mode') : t('general.dark_mode')} {t('general.mode_suffix')}
                    </span>
                  }
                  checked={theme === 'dark'}
                  onChange={toggleTheme}
                  className="mb-0"
                />
              </div>
            </NavDropdown>
          </Container>
        </Navbar>
      )}

      <Suspense fallback={null}>
        {loginModal.isOpen && (
          <LoginModal 
            show={loginModal.isOpen} 
            onHide={loginModal.close} 
            onShowSignup={signupModal.open}
            onShowPasswordReset={(email) => { setResetEmail(email); passwordResetModal.open(); }}
          />
        )}
        {signupModal.isOpen && (
          <SignupModal 
            show={signupModal.isOpen} 
            onHide={signupModal.close} 
          />
        )}
        {passwordResetModal.isOpen && (
          <RequestResetModal
            show={passwordResetModal.isOpen}
            onHide={passwordResetModal.close}
            onShowLogin={loginModal.open}
            initialEmail={resetEmail || undefined}
          />
        )}
        {termsModal.isOpen && (
          <TermsConditionsModal 
            show={termsModal.isOpen} 
            onHide={termsModal.close} 
          />
        )}
        {privacyModal.isOpen && (
          <PrivacyPolicyModal 
            show={privacyModal.isOpen} 
            onHide={privacyModal.close} 
          />
        )}
      </Suspense>
    </>
  );
});

Navigation.displayName = 'Navigation';

export default Navigation;
